import { useState } from 'react'
import { useParams } from 'react-router'
import dayjs from 'dayjs'
import range from 'lodash/range'

import ExpandAltOutlinedIcon from '@ant-design/icons/ExpandAltOutlined'

import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@components/_mui/Typography'

import { PatientPrescriptions } from '@pages/Prescriptions'
import { usePatientPrescriptions, usePatientPrescriptionsFilters } from '@pages/Prescriptions/PatientPrescriptions'
import { PrescriptionDetailsModal } from '@pages/Prescriptions/Prescription'
import { isCancelled } from '@pages/Prescriptions/Prescription/Prescription.utils'
import StatusChip from '@components/StatusChip'
import Tile from '@components/Tile'

import { Empty, IconCircleButton } from '../ProfileTab.utils'

const LIMIT = 4

export default function Rx() {
  const { id } = useParams()
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [prescriptionId, setPrescriptionId] = useState()

  const { data, isRefreshing, isPending } = usePatientPrescriptions(id, { order: 'desc', limit: LIMIT })
  const [{ rxExpanded }, updateFilters] = usePatientPrescriptionsFilters()

  const showLoading = isPending
  const showEmpty = !showLoading && data?.length === 0
  const showData = !showEmpty && data?.length > 0

  return (
    <Tile
      title="Rx"
      refreshing={isRefreshing}
      action={
        <IconCircleButton disabled={(data?.length ?? 0) === 0} size="small" onClick={() => updateFilters({ rxExpanded: true })}>
          <ExpandAltOutlinedIcon />
        </IconCircleButton>
      }
      sx={{ height: '100%' }}
    >
      <PrescriptionDetailsModal prescriptionId={prescriptionId} open={detailsOpen} onClose={() => setDetailsOpen(false)} />
      <PatientPrescriptions.Modal patientId={id} open={rxExpanded} onClose={() => updateFilters({ rxExpanded: false })} />
      {showLoading && <Loading />}
      {showEmpty && <Empty />}
      {showData && (
        <List disablePadding data-testid="prescriptions">
          {data?.map((prescription, i) => (
            <Item
              key={prescription.id}
              divider={i < data.length - 1}
              prescription={prescription}
              onClick={() => {
                setPrescriptionId(prescription.id)
                setDetailsOpen(true)
              }}
            />
          ))}
        </List>
      )}
    </Tile>
  )
}

function Item({ divider, prescription, onClick }) {
  return (
    <ListItemButton divider={divider} onClick={onClick} sx={{ opacity: isCancelled(prescription.rxStatus) ? 0.4 : 1 }}>
      <Stack direction="row" sx={{ gap: 0.5, justifyContent: 'space-between', width: '100%' }}>
        <Typography noWrap>{`${prescription.drugName}${prescription.isRefill ? ' (Refill)' : ''}`}</Typography>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <StatusChip label={`${prescription.quantity}+${prescription.refills}`} type="warning" />
          <StatusChip label={prescription.remain || 0} type="success" />
          <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {prescription.createdAt ? dayjs(prescription.createdAt).format('L') : ''}
          </Typography>
        </Stack>
      </Stack>
    </ListItemButton>
  )
}

Item.Loading = function ({ divider }) {
  return (
    <ListItemButton divider={divider}>
      <Stack direction="row" sx={{ gap: 0.5, justifyContent: 'space-between', width: '100%' }}>
        <Typography>
          <Skeleton width={150} />
        </Typography>
        <Typography>
          <Skeleton width={110} />
        </Typography>
      </Stack>
    </ListItemButton>
  )
}

function Loading() {
  return range(0, LIMIT).map((i) => <Item.Loading key={i} divider={i < LIMIT - 1} />)
}
